import * as echarts from 'echarts';
import PageCharts from '../page-charts';
import i18n from '@/i18nSetup';
import moment from 'moment';
import comOpt from './common-options.json';
import { merge } from 'lodash';

const aggregate = (pages, datesRangePosts, datesRangeStories, name) => {
  const days = PageCharts[name]({ pages, datesRangePosts, datesRangeStories }).flat();

  days.forEach(day => {
    day.formatDate = moment(day.date).format('MMM DD');
  });

  const filteredDays = days.filter(day => {
    const essentialKeys = ['date', 'day', 'formatDate'];
    return Object.keys(day).some(key => !essentialKeys.includes(key));
  });

  return filteredDays;
};

const create = function ({
  pages,
  datesRangePosts,
  datesRangeStories,
  name,
  ref,
  activePages,
  color,
  type,
}) {

  return new Promise((resolve, reject) => {
    if (activePages) {
      pages = pages.filter((p) => activePages[p.hash_id]);
    }

    const chart = echarts.init(ref);

    // Aggregate data
    const data = aggregate(pages, datesRangePosts, datesRangeStories, name);

    // Find minimum and maximum values
    const allValues = data.flatMap(item => pages.map((page, i) => item[`${name}_${i}`]).filter(value => value !== undefined && value !== null));
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    // Calculate yAxis scaling
    const yAxisMin = minValue * 0.999;
    const yAxisMax = maxValue * 1.001;

    let option = merge({}, comOpt, {
      color: [],
      xAxis: {
        type: 'time',
        axisLabel: {
          show: true,
          formatter: (value) => moment(value).format('MMM DD'),
        },
      },
      yAxis: {
        type: 'value',
        scale: true,
        min: yAxisMin.toFixed(0),
        max: yAxisMax.toFixed(0),
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          show: true,
          snap: true,
          label: { show: false },
          lineStyle: {
            color: '#C2D5FF',
            type: 'line'
          }
        },
        formatter: function (params) {
          if (params.length === 0) return '';
          const dateFormatted = moment(params[0].axisValue).format('MMM DD');
          let tooltipHtml = `<div class="tooltip"><span class="date">${dateFormatted}</span><ul class="tooltip-list">`;
          let hasData = false;
          
          if (type === 'overview-one') {
            if (params[0].data[1] != null && params[0].data[1] !== 0) {
              hasData = true;
              tooltipHtml += `<li data-value-lines="${params[0].data[1]}">
                <div class="soc-sea ${pages[0].colorName}">${i18n.t(
                'INTRO_TABS_FOLLOWERS'
              )}</div>${params[0].data[1].toLocaleString('ru-RU')}
              </li>`;
            }
          } else if ((type === 'activity' || type === 'reach') && pages.length === 1) {
            pages.forEach((page, i) => {
              const param = params.find((p) => p.seriesName == i.toString());
              if (param && param.data[1] != null && param.data[1] !== 0) {
                hasData = true;
                tooltipHtml += `<li data-value-lines="${param.data[1]}">
                  <div class="soc-sea ${color.key}"><span class="soc-ico soc-${page.social.toLowerCase()}"></span>${page.owner_name_medium}</div>${param.data[1].toLocaleString('ru-RU')}
                </li>`;
              }
            });
          } else if (
            ((type === 'activity' || type === 'reach') && pages.length > 1) ||
            type === 'overview-many'
          ) {
            pages.forEach((page, i) => {
              const param = params.find((p) => p.seriesName == i.toString());
              if (param && param.data[1] != null) {
                hasData = true;
                tooltipHtml += `<li data-value-lines="${param.data[1]}">
                  <div class="soc-sea ${page.colorName}"><span class="soc-ico soc-${page.social.toLowerCase()}"></span>${page.owner_name_medium}</div>${param.data[1].toLocaleString('ru-RU')}
                </li>`;
              }
            });
          }
          tooltipHtml += '</ul></div>';
          return hasData ? tooltipHtml : '';
        },
      },
      series: [],
    });

    if (type === 'overview-one') {
      option.color = [color.value];
    } else if ((type === 'activity' || type === 'reach') && pages.length === 1) {
      option.color = [color.value];
    }

    if (data.length) {
      pages.forEach((page, i) => {
        if (data.some((item) => item[`${name}_${i}`])) {
          let curPageColor;

          if (((type === 'activity' || type === 'reach') && pages.length > 1) || type === 'overview-many') {
            curPageColor = page.colorCode;
          } else {
            curPageColor = color?.value || page.colorCode || '#000'; // Default to black
          }

          const seriesItem = {
            name: i.toString(),
            type: 'line',
            data: data.map((item) => [item.date, item[`${name}_${i}`]]),
            smooth: 0.4,
            connectNulls: true,
            lineStyle: {
              width: 1,
              color: curPageColor,
            },
            itemStyle: {
              color: curPageColor,
            },
            showSymbol: false,
            symbolSize: 6,
            emphasis: {
              focus: 'series',
              symbol: 'circle',
              itemStyle: {
                color: curPageColor,
                borderColor: `rgba(${echarts.color.parse(curPageColor).slice(0, 3).join(',')}, 0.3)`,
                borderWidth: 10,
                opacity: 1,
              },
            },
          };

          if (pages.length === 1) {
            seriesItem.areaStyle = {
              opacity: 1,
              //color: color?.value || page.colorCode,
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: `rgba(${echarts.color.parse(curPageColor).slice(0, 3).join(',')}, 0.6)`,
                },
                {
                  offset: 1,
                  color: `rgba(${echarts.color.parse(curPageColor).slice(0, 3).join(',')}, 0.1)`,
                }
              ])
            };
          }

          option.series.push(seriesItem);
        }
      });
    }

    chart.setOption(option);
    resolve(chart);
    window.addEventListener('resize', () => {
      chart.resize();
    });
  });
};

export default create;
